/*
 * Naturlabs cart page (sections/nl-cart-main.liquid).
 * <nl-cart-page data-cart-section="{section id}">: the element itself is kept, its children are re-rendered by
 * NL.cart (Section Rendering API) after every cart change, so all listeners are delegated on the host.
 *   - quantity: [data-line-quantity][data-line-key] change (debounced) -> NL.cart.change(key, qty)
 *   - remove:   [data-cart-remove][data-line-key] click (a link to url_to_remove without JS) -> row collapses, qty 0
 *   - coupon:   form[data-coupon-form] -> /discount/CODE?redirect=<cart> (discounts are applied in checkout)
 *   - note:     textarea[data-cart-note] -> NL.cart.update({}, { note }) debounced
 *   - sticky:   [data-cart-sticky] bar on phones while the summary CTA is below the viewport
 *   - busy:     the row is dimmed with a spinner on the pressed control, checkout waits until the cart is saved
 *   - errors:   cart API messages (422 stock limits) are shown inline under the row after a refresh
 *   - focus:    after the re-render focus returns to the same control (or the next row)
 *   - live:     polite announcement from the server rendered [data-cart-page-message] (prices without ,00)
 *   - delivery: the Globe app keeps checkout blocked until a delivery method is saved (aria-disabled on its clone of
 *               the button). A tap on the blocked button scrolls to the delivery cards, focuses the first one and
 *               outlines them for 1.2 s. The summary adds the price of the chosen method and then says "Celkem".
 *   - slot:     [data-globe-slot] keeps the height of the delivery cards until Globe inserts them (no layout shift)
 * Depends on assets/nl-app.js (window.NL). Respects prefers-reduced-motion.
 */
(() => {
  'use strict';

  if (customElements.get('nl-cart-page')) return;

  const reduced = () =>
    window.NL && window.NL.motion
      ? window.NL.motion.reduced
      : window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const ALERT_ICON =
    '<svg class="nl-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>';

  const kindOf = (control) => {
    if (!control || !control.matches) return 'input';
    if (control.matches('[data-cart-remove]')) return 'remove';
    if (control.matches('[data-qty-step="1"]')) return 'plus';
    if (control.matches('[data-qty-step="-1"]')) return 'minus';
    return 'input';
  };

  const controlOf = (row, kind) => {
    if (!row) return null;
    if (kind === 'remove') return row.querySelector('[data-cart-remove]');
    if (kind === 'plus') return row.querySelector('[data-qty-step="1"]');
    if (kind === 'minus') return row.querySelector('[data-qty-step="-1"]');
    return row.querySelector('[data-line-quantity]');
  };

  class NLCartPage extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.pending = new Map();
      this.live = this.parentElement && this.parentElement.querySelector('[data-cart-live]');

      this.busy = 0;
      this.pickupStateVersion = 0;
      this.pickupStateLoading = true;
      this.savedDeliveryMethod = '';
      this.pressed = { key: null, kind: null };
      this.addEventListener(
        'click',
        (event) => {
          const control = event.target.closest('li[data-line-key] :is([data-qty-step], [data-cart-remove])');
          if (!control) return;
          const row = control.closest('li[data-line-key]');
          this.pressed = { key: row ? row.dataset.lineKey : null, kind: kindOf(control) };
        },
        true
      );
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('submit', (event) => this.onSubmit(event));
      // Checkout waits until the cart is saved (summary and sticky buttons submit the cart form with name=checkout)
      this.onCheckoutGuard = (event) => {
        const submitter = event.submitter;
        const cartForm = event.target && this.contains(event.target) && event.target.matches('.nl-cart-page__form');
        if (cartForm && (!submitter || submitter.name === 'checkout') && this.guardUnavailablePickup(event)) return;
        if (this.busy > 0 && submitter && submitter.name === 'checkout') event.preventDefault();
      };
      document.addEventListener('submit', this.onCheckoutGuard, true);
      this.addEventListener('input', (event) => this.onInput(event));

      this.onCartUpdated = (event) => {
        const detail = event.detail || {};
        if (!detail.cart) return;
        this.acceptDeliveryCart(detail.cart);
        this.announce(detail.cart);
        this.setupSticky();
        this.refreshRelated();
      };
      document.addEventListener('cart:updated', this.onCartUpdated);
      this.setupSticky();

      // Blocked checkout (Globe): explain and lead to the delivery cards instead of a silent dimmed button. Capture on
      // the document runs before the click handler Globe puts on its clone, so its own message does not scroll too.
      this.onGateClick = (event) => {
        const button = event.target.closest && event.target.closest('[name="checkout"], [data-cart-checkout]');
        if (!button || !this.contains(button)) return;
        if (this.guardUnavailablePickup(event)) return;
        if (button.getAttribute('aria-disabled') !== 'true') return;
        const box = this.deliveryBox();
        if (!box) return;
        event.preventDefault();
        event.stopPropagation();
        this.revealDelivery(box);
      };
      document.addEventListener('click', this.onGateClick, true);
      this.onDeliveryChange = (event) => {
        if (!event.target.matches('input[name^="globe-delivery-method"]')) return;
        this.pickupStateLoading = true;
        this.pickupStateVersion += 1;
        this.syncPickupNotice();
        window.clearTimeout(this.pickupReadTimer);
        this.pickupReadTimer = window.setTimeout(() => this.readDeliveryCart(), 500);
      };
      document.addEventListener('change', this.onDeliveryChange, true);
      this.readDeliveryCart();

      // Delivery price in the summary follows the Globe cards (their markup and prices change after load)
      this.syncSoon = () => {
        window.clearTimeout(this.syncTimer);
        this.syncTimer = window.setTimeout(() => this.syncDelivery(), 80);
      };
      this.deliveryObserver = new MutationObserver(this.syncSoon);
      this.deliveryObserver.observe(this, { childList: true, subtree: true, characterData: true });
      this.addEventListener('change', (event) => {
        if (event.target.closest('[id^="globe-delivery-selector-container"]')) this.syncSoon();
      });
      this.syncDelivery();

      // Reserved space for the delivery cards: drop it when Globe never renders them (other country, B2B, app off)
      const dropSlot = () => {
        window.setTimeout(() => {
          if (!this.deliveryBox()) this.querySelectorAll('[data-globe-slot]').forEach((slot) => slot.remove());
        }, 8000);
      };
      if (document.readyState === 'complete') dropSlot();
      else window.addEventListener('load', dropSlot, { once: true });
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.onCartUpdated);
      document.removeEventListener('submit', this.onCheckoutGuard, true);
      document.removeEventListener('click', this.onGateClick, true);
      document.removeEventListener('change', this.onDeliveryChange, true);
      window.clearTimeout(this.pickupReadTimer);
      if (this.deliveryObserver) this.deliveryObserver.disconnect();
      if (this.stickyObserver) this.stickyObserver.disconnect();
      if (this.relatedController) this.relatedController.abort();
      this._bound = false;
    }

    get cart() {
      return window.NL && window.NL.cart;
    }

    /* ---- Remove ---- */
    onClick(event) {
      const delivery = event.target.closest('[data-cart-scroll-delivery]');
      if (delivery) {
        event.preventDefault();
        const box = this.deliveryBox() || this.querySelector('[data-globe-slot]') || this.querySelector('[data-cart-summary]');
        if (box) this.revealDelivery(box);
        return;
      }
      const remove = event.target.closest('[data-cart-remove]');
      if (!remove || !this.cart) return;
      event.preventDefault();
      const row = remove.closest('.nl-cart-line') || remove.closest('li[data-line-key]');
      this.collapse(row);
      this.pressed = { key: remove.dataset.lineKey, kind: 'remove' };
      this.updateLine(remove.dataset.lineKey, 0, row);
    }

    collapse(row) {
      if (!row || reduced() || !row.animate) return;
      const height = row.getBoundingClientRect().height;
      row.style.overflow = 'hidden';
      row.animate(
        [
          { opacity: 1, height: `${height}px` },
          { opacity: 0, height: '0px', paddingTop: '0px', paddingBottom: '0px' },
        ],
        { duration: 360, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'forwards' }
      );
    }

    /* ---- Quantity ---- */
    onChange(event) {
      const input = event.target.closest('[data-line-quantity]');
      if (!input || !this.cart) return;
      const key = input.dataset.lineKey;
      window.clearTimeout(this.pending.get(key));
      this.pending.set(
        key,
        window.setTimeout(() => {
          const quantity = Math.max(0, parseInt(input.value, 10) || 0);
          const row = input.closest('.nl-cart-line') || input.closest('li[data-line-key]');
          if (quantity === 0) this.collapse(row);
          this.updateLine(key, quantity, row);
        }, 320)
      );
    }

    async updateLine(key, quantity, row) {
      const kind = this.pressed.key === key && this.pressed.kind ? this.pressed.kind : 'input';
      this.pressed = { key: null, kind: null };
      const rows = Array.from(this.querySelectorAll('li[data-line-key]'));
      const index = Math.max(0, rows.indexOf(row));
      const hadFocus = this.contains(document.activeElement);
      this.clearErrors();
      this.busy += 1;
      this.setBusy(row, kind, true);
      try {
        await this.cart.change(key, quantity, { source: 'cart-page' });
        this.restoreFocus(key, kind, index, hadFocus);
      } catch (error) {
        const strings = (window.NL && window.NL.strings) || {};
        const message = (error && error.message) || strings.errorGeneric || '';
        try {
          await this.cart.refresh('cart-page-error');
        } catch (refreshError) {
          /* offline: keep the markup */
        }
        this.showError(key, message);
        this.restoreFocus(key, kind, index, hadFocus);
      } finally {
        this.busy = Math.max(0, this.busy - 1);
        if (row && row.isConnected) this.setBusy(row, kind, false);
        if (this.busy === 0) {
          this.querySelectorAll('[data-cart-checkout]').forEach((button) => button.removeAttribute('aria-busy'));
        }
      }
    }

    setBusy(row, kind, busy) {
      if (busy) this.querySelectorAll('[data-cart-checkout]').forEach((button) => button.setAttribute('aria-busy', 'true'));
      if (!row) return;
      if (busy) row.setAttribute('aria-busy', 'true');
      else row.removeAttribute('aria-busy');
      const control = controlOf(row, kind);
      if (control && kind !== 'input') control.classList.toggle('is-pressed', busy);
    }

    rowByKey(key) {
      return key ? this.querySelector(`li[data-line-key="${CSS.escape(String(key))}"]`) : null;
    }

    restoreFocus(key, kind, index, hadFocus) {
      // Also restore when focus fell to the body (a stepper button that turns disabled at 0 drops focus)
      const active = document.activeElement;
      const lost = !active || active === document.body;
      if (!hadFocus && !lost) return;
      const row = this.rowByKey(key);
      let target = null;
      if (row) {
        target = controlOf(row, kind);
        if (!target || target.disabled) target = controlOf(row, 'input');
      } else {
        const rows = Array.from(this.querySelectorAll('li[data-line-key]'));
        const next = rows[Math.min(index, rows.length - 1)];
        target = next ? next.querySelector('.nl-cart-row__title') : null;
        if (!target) {
          const heading = this.closest('section') && this.closest('section').querySelector('h1');
          if (heading) {
            heading.setAttribute('tabindex', '-1');
            target = heading;
          }
        }
      }
      if (target) target.focus();
    }

    clearErrors() {
      this.querySelectorAll('.nl-cart-page__error').forEach((el) => el.remove());
    }

    showError(key, message) {
      if (!message) return;
      this.clearErrors();
      const row = this.rowByKey(key);
      const slot = row && row.querySelector('[data-line-error-slot]');
      if (!slot) {
        if (window.NL && window.NL.toast) window.NL.toast(message, { variant: 'error' });
        return;
      }
      const box = document.createElement('p');
      box.className = 'nl-cart-page__error';
      box.setAttribute('role', 'alert');
      box.innerHTML = ALERT_ICON;
      const text = document.createElement('span');
      text.textContent = message;
      box.append(text);
      slot.append(box);
    }

    /* ---- Coupon ---- */
    onSubmit(event) {
      const form = event.target.closest('[data-coupon-form]');
      if (!form) return;
      event.preventDefault();
      const input = form.querySelector('input[name="discount"]');
      const error = form.querySelector('[data-coupon-error]');
      const code = input ? input.value.trim() : '';
      if (!code) {
        if (input) {
          input.setAttribute('aria-invalid', 'true');
          input.focus();
        }
        if (error) {
          error.textContent = this.dataset.requiredMessage || '';
          error.hidden = false;
        }
        return;
      }
      if (input) input.removeAttribute('aria-invalid');
      if (error) error.hidden = true;
      const button = form.querySelector('[type="submit"]');
      if (button) button.setAttribute('aria-busy', 'true');
      const redirect = this.dataset.cartUrl || '/cart';
      window.location.assign(`/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(redirect)}`);
    }

    /* ---- Note ---- */
    onInput(event) {
      const field = event.target.closest('[data-cart-note]');
      if (field) {
        window.clearTimeout(this.noteTimer);
        this.noteTimer = window.setTimeout(() => {
          if (this.cart) this.cart.update({}, { note: field.value, source: 'cart-note' }).catch(() => {});
        }, 600);
        return;
      }
      const coupon = event.target.closest('[data-coupon-form] input');
      if (coupon && coupon.getAttribute('aria-invalid') === 'true' && coupon.value.trim()) {
        coupon.removeAttribute('aria-invalid');
        const error = coupon.form && coupon.form.querySelector('[data-coupon-error]');
        if (error) error.hidden = true;
      }
    }

    /* ---- Live region with the new total ---- */
    announce(cart) {
      if (!this.live || !cart) return;
      const source = this.querySelector('[data-cart-page-message]');
      const message = source ? source.textContent.replace(/\s+/g, ' ').trim() : '';
      window.clearTimeout(this.announceTimer);
      this.live.textContent = '';
      this.announceTimer = window.setTimeout(() => {
        this.live.textContent = message;
      }, 120);
    }

    /* ---- Delivery (Globe cards in the items card) ---- */
    // Temporary release gate. Only an explicit backend readiness release may set data-pickup-ready="true".
    pickupUnavailable() {
      if (this.dataset.pickupReady === 'true') return false;
      const checked = this.querySelector('input[name^="globe-delivery-method"]:checked');
      return this.pickupStateLoading || this.savedDeliveryMethod === 'pickup' || (checked && checked.value === 'pickup');
    }

    acceptDeliveryCart(cart) {
      this.pickupStateVersion += 1;
      this.savedDeliveryMethod = String((cart.attributes || {}).globe_DeliveryMethod || '').toLowerCase();
      this.pickupStateLoading = false;
      this.syncPickupNotice();
    }

    async readDeliveryCart() {
      const version = this.pickupStateVersion;
      if (!this.cart || !this.cart.get) return;
      try {
        const cart = await this.cart.get();
        // A later cart event or selection owns the state, never replace it with a delayed initial read.
        if (version === this.pickupStateVersion && this.isConnected) this.acceptDeliveryCart(cart);
      } catch (error) { /* Keep checkout guarded until a fresh cart read succeeds. */ }
    }

    syncPickupNotice() {
      let notice = this.querySelector('[data-pickup-unavailable-notice]');
      const checked = this.querySelector('input[name^="globe-delivery-method"]:checked');
      const show = this.dataset.pickupReady !== 'true' &&
        (this.savedDeliveryMethod === 'pickup' || (checked && checked.value === 'pickup'));
      if (!notice && show) {
        const anchor = this.deliveryBox() || this.querySelector('[data-cart-summary]');
        if (!anchor) return;
        notice = document.createElement('p');
        notice.dataset.pickupUnavailableNotice = '';
        notice.setAttribute('role', 'alert');
        anchor.appendChild(notice);
      }
      if (notice) {
        const message = this.dataset.pickupUnavailableMessage || 'Doručení na výdejní místo teď není dostupné. Vyberte prosím doručení na adresu.';
        if (notice.textContent !== message) notice.textContent = message;
        notice.hidden = !show;
      }
    }

    guardUnavailablePickup(event) {
      if (!this.pickupUnavailable()) return false;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.syncPickupNotice();
      const box = this.deliveryBox();
      if (box) this.revealDelivery(box);
      // A retry reads fresh saved attributes; this never writes or silently changes the delivery choice.
      this.readDeliveryCart();
      return true;
    }

    deliveryBox() {
      const boxes = this.querySelectorAll('[id^="globe-delivery-selector-container"]');
      for (let i = 0; i < boxes.length; i += 1) {
        if (boxes[i].getClientRects().length) return boxes[i];
      }
      return null;
    }

    revealDelivery(box) {
      box.scrollIntoView({ block: 'center', behavior: reduced() ? 'auto' : 'smooth' });
      const first = box.querySelector(
        '.globe-delivery-option input[type="radio"], .globe-delivery-option button, .globe-delivery-option [tabindex="0"]'
      );
      if (first) first.focus({ preventScroll: true });
      box.classList.remove('nl-delivery-attention');
      void box.offsetWidth;
      box.classList.add('nl-delivery-attention');
      window.clearTimeout(this.attentionTimer);
      this.attentionTimer = window.setTimeout(() => box.classList.remove('nl-delivery-attention'), 1200);
    }

    // Price of the chosen method from the Globe card (null while nothing is chosen or the price is still loading)
    chosenShipping() {
      const box = this.deliveryBox();
      if (!box) return null;
      const checked = box.querySelector('input[type="radio"][name^="globe-delivery-method"]:checked');
      if (!checked) return null;
      const prefix = checked.value === 'pickup' ? 'globe-price-pickup-sel-' : 'globe-price-home-';
      const price = box.querySelector(`[id^="${prefix}"]`);
      const text = price ? price.textContent.replace(/\s+/g, ' ').trim() : '';
      if (!text || !price.getClientRects().length) return null;
      if (/zdarma|free|gratis|kostenlos/i.test(text)) return 0;
      return NLCartPage.priceCents(text);
    }

    // Globe prints the price in the format of the language: "99,00 K\u010d" (cs), "99,00 CZK" (de), "CZK 99.00" (en),
    // also "1 499,00 K\u010d" or "CZK 1,499.00". The decimal separator is the last "." or "," followed by two digits
    // (or one) at the end of the number, a thousands group always has three; every other dot, comma, space or
    // apostrophe groups thousands. Removing all dots made "CZK 99.00" 9 900 K\u010d on the English cart.
    static priceCents(text) {
      const found = String(text).match(/\d[\d\s\u00a0\u202f.,'\u2019]*/);
      if (!found) return null;
      const num = found[0].replace(/[\s\u00a0\u202f'\u2019]/g, '').replace(/[.,]+$/, '');
      const dec = num.match(/[.,](\d{1,2})$/);
      const whole = (dec ? num.slice(0, -dec[0].length) : num).replace(/[.,]/g, '');
      if (!/^\d+$/.test(whole)) return null;
      return Number(whole) * 100 + (dec ? Number(dec[1].padEnd(2, '0')) : 0);
    }

    money(cents) {
      const nl = window.NL || {};
      return nl.formatMoney ? nl.formatMoney(cents, { trimZeros: true }) : String(Number(cents || 0) / 100);
    }

    syncDelivery() {
      this.syncPickupNotice();
      const summary = this.querySelector('[data-cart-summary]');
      if (!summary) return;
      const set = (el, text) => {
        if (el && el.textContent !== text) el.textContent = text;
      };
      const value = summary.querySelector('[data-cart-shipping-value]');
      const labels = this.querySelectorAll('[data-cart-total-label]');
      const totals = this.querySelectorAll('[data-cart-total-value]');
      // Server render is the default state; remember it once per render
      [value, ...labels, ...totals].forEach((el) => {
        if (el && el.dataset.serverText === undefined) el.dataset.serverText = el.textContent;
      });
      const shipping = value ? this.chosenShipping() : null;
      if (shipping === null) {
        [value, ...labels, ...totals].forEach((el) => el && set(el, el.dataset.serverText));
        return;
      }
      const base = Number(summary.dataset.totalCents) || 0;
      set(value, shipping === 0 ? this.dataset.freeLabel || '' : this.money(shipping));
      labels.forEach((el) => set(el, this.dataset.totalLabel || el.dataset.serverText));
      totals.forEach((el) => set(el, this.money(base + shipping)));
    }

    async refreshRelated() {
      const region = document.querySelector('[data-cart-related-section]');
      if (!region) return;
      const id = region.dataset.cartRelatedSection;
      if (this.relatedController) this.relatedController.abort();
      this.relatedController = new AbortController();
      try {
        const response = await fetch(`${this.dataset.cartUrl || '/cart'}?section_id=${encodeURIComponent(id)}`, {
          credentials: 'same-origin', signal: this.relatedController.signal,
        });
        if (!response.ok) return;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const updated = doc.querySelector('[data-cart-related-section]');
        if (updated) region.replaceWith(updated);
      } catch (error) { /* retain the previous cards on a connection failure */ }
    }

    /* ---- Sticky checkout bar (phones and tablets) ---- */
    setupSticky() {
      if (this.stickyObserver) this.stickyObserver.disconnect();
      const bar = this.querySelector('[data-cart-sticky]');
      const target = this.querySelector('[data-cart-summary] [data-cart-total-value]');
      if (!bar || !target || !('IntersectionObserver' in window)) return;
      this.stickyObserver = new IntersectionObserver(
        ([entry]) => {
          const below = !entry.isIntersecting && entry.boundingClientRect.top > 0;
          bar.classList.toggle('is-visible', below);
          bar.setAttribute('aria-hidden', String(!below));
          const button = bar.querySelector('[data-cart-scroll-delivery]');
          if (button) button.tabIndex = below ? 0 : -1;
        },
        { threshold: 0 }
      );
      this.stickyObserver.observe(target);
    }
  }

  customElements.define('nl-cart-page', NLCartPage);
})();
